import { Code, ConnectError, type Interceptor } from '@connectrpc/connect'
import { WebSessionService } from '@/gen/webgame/v1/session_pb'
import i18n from '@/i18n'

/** The server localizes coded errors and copy by this header. */
export const acceptLanguageInterceptor: Interceptor = (next) => (req) => {
  const lang = i18n.resolvedLanguage ?? i18n.language
  if (lang) req.header.set('accept-language', lang)
  return next(req)
}

export interface AuthInterceptorDeps {
  getAccessToken: () => string | null
  /**
   * Single-flight refresh. Receives the token the failed call carried, so a
   * call that raced an already-completed refresh just retries with the new
   * token instead of rotating it a second time. Resolves to the token to
   * retry with, or null when the session is gone.
   */
  refresh: (usedToken: string | null) => Promise<string | null>
}

/** WebSessionService is public: it must never carry (or refresh) a bearer. */
export function isPublicService(typeName: string): boolean {
  return typeName === WebSessionService.typeName
}

/**
 * Bearer auth with one retry. On Unauthenticated the call waits for the
 * shared refresh and is re-sent ONCE with the fresh token; a second
 * Unauthenticated is the caller's problem (the refresher has already signed
 * the player out by then).
 */
export function createAuthInterceptor(deps: AuthInterceptorDeps): Interceptor {
  return (next) => async (req) => {
    if (isPublicService(req.service.typeName)) return next(req)
    const token = deps.getAccessToken()
    if (token) req.header.set('authorization', `Bearer ${token}`)
    try {
      return await next(req)
    } catch (err) {
      const ce = ConnectError.from(err)
      if (ce.code !== Code.Unauthenticated || req.stream) throw err
      const fresh = await deps.refresh(token)
      if (!fresh) throw err
      req.header.set('authorization', `Bearer ${fresh}`)
      return await next(req)
    }
  }
}
